/**
 * User-facing copy for auth endpoint errors (login, accept-invite).
 *
 * The backend puts a machine code in the `error` field for some failures
 * (LOGIN_LOCKED, RATE_LIMITED, TURNSTILE_FAILED) — usually alongside a
 * sanitized `message` — and a plain sentence for others ("Invalid
 * credentials"). Precedence: message > known-code mapping > sentence
 * passthrough. A raw code must never reach the user; unknown codes fall back
 * to a generic message.
 */

export const GENERIC_AUTH_ERROR = 'Something went wrong. Please try again.';

/** Known auth error codes → user-facing copy (mirrors the backend's own message wording). */
const AUTH_CODE_MESSAGES: Record<string, string> = {
  LOGIN_LOCKED: 'Too many login attempts. Please try again later.',
  RATE_LIMITED: 'Too many requests. Please try again later.',
  TURNSTILE_FAILED: 'Security verification failed. Please try again.',
};

/** SCREAMING_SNAKE — the shape of an internal machine code, not a sentence. */
const CODE_PATTERN = /^[A-Z0-9]+(?:_[A-Z0-9]+)*$/;

export function authErrorMessage(
  data: { error?: unknown; message?: unknown } | null | undefined,
  fallback: string = GENERIC_AUTH_ERROR
): string {
  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }
  const error = data?.error;
  if (typeof error === 'string' && error.trim()) {
    const mapped = AUTH_CODE_MESSAGES[error];
    if (mapped) return mapped;
    // A sanitized sentence from the backend renders as-is; a machine code
    // that is not in the map must not be shown raw.
    if (!CODE_PATTERN.test(error)) return error;
  }
  return fallback;
}
